import React, { useEffect, useState } from 'react';
import { Card, Button, Table, Form, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { LUUTRUHOSOTIENSI_API, DANHMUCHOSOTIENSI_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { Get, Post, Put, DeleteSingle } from '../../../../utils/httpProvider';
import DocumentInput from '../../../../components/document-input/DocumentInput';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { messageToast, status200 } from '../../../../utils/define';

const LuuTruHoSoTienSiModal = ({ danhMucId, tenDanhMuc, danhMucInfo }) => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    const [tenFile, setTenFile] = useState('');
    const [ngayTao, setNgayTao] = useState('');
    const [fileList, setFileList] = useState([]);

    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        setIsEditing(false);
        setCurrentRecord(null);
        setTenFile('');
        setNgayTao('');
        setFileList([]);
    }, [danhMucId]);

    const { data, refetch, isLoading } = useQuery(
        ['getLuuTruHoSoTienSi', danhMucId],
        () => {
            if (!danhMucId) return Promise.resolve(null);
            return Get(LUUTRUHOSOTIENSI_API.GET_PAGING, null, {
                DanhMucHoSoTienSiId: danhMucId,
                PageIndex: 1,
                PageSize: 1000
            });
        },
        {
            enabled: !!danhMucId,
            cacheTime: 0,
            staleTime: 0
        }
    );

    const items = data?.data?.$values ? data.data.$values : (data?.data || []);

    useEffect(() => {
        if (items && items.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = items.find(item => item.id === prev.id);
                    return fresh || items[0];
                }
                return items[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [data]);

    const createMutation = useMutation((payload) => Post(LUUTRUHOSOTIENSI_API.CREATE, null, payload));
    const updateMutation = useMutation(({ id, payload }) => Put(LUUTRUHOSOTIENSI_API.UPDATE_ITEM, id, payload));
    const deleteMutation = useMutation((id) => DeleteSingle(LUUTRUHOSOTIENSI_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setCurrentRecord(null);
        setTenFile('');
        const now = new Date();
        setNgayTao(new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().split('T')[0]);
        setFileList([]);
        setIsEditing(true);
    };

    const handleEdit = (record) => {
        setCurrentRecord(record);
        setTenFile(record.tenFile || '');
        setNgayTao(new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().split('T')[0]);
        
        let paths = [];
        let rawPath = record.fileLuuTru;
        if (rawPath) {
            try {
                const parsed = JSON.parse(rawPath);
                if (Array.isArray(parsed)) {
                    paths = parsed;
                } else {
                    paths = [rawPath];
                }
            } catch(e) {
                paths = [rawPath];
            }
            
            const loadedFiles = paths.map(p => {
                let name = p.split('/').pop() || 'File';
                name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                return { filePath: p, name: name };
            });
            setFileList(loadedFiles);
        } else {
            setFileList([]);
        }
        setIsEditing(true);
    };

    const handleDelete = (id) => {
        setIdToDelete(id);
        setOpenConfirmDelete(true);
    };

    const confirmDelete = () => {
        deleteMutation.mutate(idToDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201 || res.status === 204) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(messageToast.messageToastDeleteError);
                }
            },
            onError: () => toast.error(messageToast.messageToastTryCatch),
            onSettled: () => setOpenConfirmDelete(false)
        });
    };

    const updateDanhMucDate = () => {
        if (!danhMucInfo) return;
        const currentDate = new Date();
        const localDateStr = new Date(currentDate.getTime() - (currentDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        
        const payloadDanhMuc = {
            TenLoaiHoSo: danhMucInfo.tenLoaiHoSo,
            NgayTao: localDateStr,
            MoTa: danhMucInfo.moTa
        };
        
        Put(`${DANHMUCHOSOTIENSI_API.UPDATE_ITEM}/${danhMucId}`, null, payloadDanhMuc).then(() => {
        }).catch(err => {
            console.error("Lỗi khi cập nhật ngày danh mục:", err);
        });
    };

    const handleSave = () => {
        if (!tenFile.trim()) {
            toast.warning('Vui lòng nhập tên file');
            return;
        }
        if (fileList.length === 0) {
            toast.warning('Vui lòng đính kèm ít nhất 1 file');
            return;
        }

        setIsSaving(true);
        const filePaths = fileList.map(f => f.filePath || f.url || f.name || f);
        const fileLuuTruValue = JSON.stringify(filePaths);

        const payload = {
            DanhMucHoSoTienSiId: danhMucId,
            TenFile: tenFile,
            NgayTao: ngayTao || null,
            FileLuuTru: fileLuuTruValue
        };

        if (currentRecord) {
            updateMutation.mutate(
                { id: currentRecord.id, payload },
                {
                    onSuccess: (res) => {
                        setIsSaving(false);
                        if (res.status === 200 || res.status === 204) {
                            toast.success('Cập nhật hồ sơ thành công');
                            updateDanhMucDate();
                            setIsEditing(false);
                            refetch();
                        } else {
                            toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                        }
                    },
                    onError: () => {
                        setIsSaving(false);
                        toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                    }
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: (res) => {
                    setIsSaving(false);
                    if (res.status === 200 || res.status === 201) {
                        toast.success('Thêm mới hồ sơ thành công');
                        updateDanhMucDate();
                        setIsEditing(false);
                        refetch();
                    } else {
                        toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                    }
                },
                onError: () => {
                    setIsSaving(false);
                    toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                }
            });
        }
    };

    const renderFile = (pathStr) => {
        if (!pathStr) return <span className="text-muted">Không có file</span>;
        
        let paths = [];
        try {
            const parsed = JSON.parse(pathStr);
            if (Array.isArray(parsed)) {
                paths = parsed;
            } else {
                paths = [pathStr];
            }
        } catch(e) {
            paths = [pathStr];
        }

        return paths.map((actualPath, idx) => {
            const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
            const safePath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
            const fileUrl = actualPath.startsWith('http') ? actualPath : `${baseUrl}${safePath}`;
            let name = actualPath.split('/').pop() || 'File';
            name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
            
            return (
                <div key={idx} className="mb-1">
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left" style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                        <i className="fas fa-paperclip mr-1"></i> {name}
                    </a>
                </div>
            );
        });
    };

    return (
        <Card className="card-sticky-layout card-sticky-layout-lg">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h4 className="font-weight-bold m-0">Lưu trữ hồ sơ - {tenDanhMuc}</h4>
                    {!isEditing && (
                        <Button variant="success" onClick={handleAdd}>
                            Thêm mới
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column" style={{ overflowY: 'auto' }}>
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '50px' }}>STT</th>
                                        <th>Tên file</th>
                                        <th className="text-center" style={{ width: '150px', whiteSpace: 'nowrap' }}>Ngày cập nhật</th>
                                        <th>File đính kèm</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center">Chưa có hồ sơ lưu trữ nào</td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => (
                                            <tr 
                                                key={item.id}
                                                onClick={() => setSelectedRow(item)}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                            >
                                                <td className="text-center">{index + 1}</td>
                                                <td>{item.tenFile}</td>
                                                <td className="text-center">{item.ngayTao ? new Date(item.ngayTao).toLocaleDateString('vi-VN') : ''}</td>
                                                <td>{renderFile(item.fileLuuTru)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        )}
            </Card.Body>

            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="info" 
                            size="sm" 
                            className="mr-2" 
                            onClick={() => handleEdit(selectedRow)}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => handleDelete(selectedRow?.id)}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-trash mr-1"></i> Xóa
                        </Button>
                    </div>
                </Card.Footer>
            )}

            <Modal show={isEditing} onHide={() => setIsEditing(false)} backdrop="static" size="lg">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>{currentRecord ? 'Cập nhật' : 'Thêm mới'} file hồ sơ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Tên file <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="Nhập tên file" 
                            value={tenFile} 
                            onChange={(e) => setTenFile(e.target.value)} 
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Ngày tạo</Form.Label>
                        <Form.Control 
                            type="date" 
                            value={ngayTao} 
                            onChange={(e) => setNgayTao(e.target.value)} 
                        />
                    </Form.Group>
                    <DocumentInput
                        value={fileList}
                        multiple={true}
                        onFilesChange={setFileList}
                        labelTitle={<>File đính kèm <span className="text-danger">*</span></>}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={isSaving}>
                        Đóng
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                isOpen={openConfirmDelete}
                confirmType="delete"
                goBackAfterDelete={false}
                handleAccept={confirmDelete}
                handleCancel={() => setOpenConfirmDelete(false)}
                content="Bạn có chắc chắn muốn xóa hồ sơ này?"
            />
        </Card>
    );
};

export default LuuTruHoSoTienSiModal;
